import type { ComponentType, CSSProperties } from 'react';
// Round 7: per-icon subpath imports (not the '@phosphor-icons/react' barrel), so the
// static search panel island only bundles the icons it actually uses instead of the
// whole ~1300-icon package (the barrel's index.es.js bundles every icon's path data as
// one non-tree-shakeable object, confirmed by its own file size vs. one icon file's).
import { Brain } from '@phosphor-icons/react/dist/csr/Brain';
import { Broadcast } from '@phosphor-icons/react/dist/csr/Broadcast';
import { Browser } from '@phosphor-icons/react/dist/csr/Browser';
import { ChartLineUp } from '@phosphor-icons/react/dist/csr/ChartLineUp';
import { ChatCircleDots } from '@phosphor-icons/react/dist/csr/ChatCircleDots';
import { Cloud } from '@phosphor-icons/react/dist/csr/Cloud';
import { Code } from '@phosphor-icons/react/dist/csr/Code';
import { Database } from '@phosphor-icons/react/dist/csr/Database';
import { Detective } from '@phosphor-icons/react/dist/csr/Detective';
import { Gauge } from '@phosphor-icons/react/dist/csr/Gauge';
import { GitBranch } from '@phosphor-icons/react/dist/csr/GitBranch';
import { Lightning } from '@phosphor-icons/react/dist/csr/Lightning';
import { MagicWand } from '@phosphor-icons/react/dist/csr/MagicWand';
import { PaintBrush } from '@phosphor-icons/react/dist/csr/PaintBrush';
import { Robot } from '@phosphor-icons/react/dist/csr/Robot';
import { Scales } from '@phosphor-icons/react/dist/csr/Scales';
import { ShieldCheck } from '@phosphor-icons/react/dist/csr/ShieldCheck';
import { Stack } from '@phosphor-icons/react/dist/csr/Stack';
import { Target } from '@phosphor-icons/react/dist/csr/Target';
import { UsersThree } from '@phosphor-icons/react/dist/csr/UsersThree';

/**
 * TEC-512 editorial-rail redesign: skill icon lookup for `skillsAndTechnologies`, following the
 * exact pattern `BENEFIT_ICONS` proves out in `web-app/components/filters/FilterBuildingBlocks.tsx`.
 * Values are free text an LLM extracts from job postings, not a fixed enum, so this covers as
 * many common values (matched case-insensitively) as reasonably curatable, then falls back to a
 * category bucket by keyword; not one generic icon for everything, since skills span far more
 * categories than benefits do (BENEFIT_ICONS's single `Gift` fallback would be too coarse here).
 * Generic Phosphor outline icons here (the curated/category fallback), not real tech logos: this
 * was the design review's original call for the whole page. User decision, 2026-09-27: the JDP's
 * skill/benefit chips now show the SAME real brand-colour tiles the search panel does
 * (`utils/search-panel/facet-icon.tsx`'s `FacetIcon`, for now: brand-colour tiles on both
 * surfaces, not the mono-on-JDP/colour-in-search split floated earlier in that same discussion),
 * so this module's own icons only remain the fallback FacetIcon itself falls back to for a
 * non-brand skill, a monogram-only brand (Azure, Salesforce, Java, C#, Tableau, Power BI, Oracle:
 * a plain letter tile reads worse than a real category icon at chip size), or wherever else in
 * the app still calls `getSkillIcon` directly instead of going through `FacetIcon`.
 */
type SkillIconComponent = ComponentType<{ className?: string; weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone'; style?: CSSProperties }>;

const SKILL_ICONS: Record<string, SkillIconComponent> = {
  // Languages
  javascript: Code,
  typescript: Code,
  python: Code,
  java: Code,
  'c++': Code,
  'c#': Code,
  go: Code,
  golang: Code,
  rust: Code,
  ruby: Code,
  php: Code,
  kotlin: Code,
  swift: Code,
  scala: Code,
  'c': Code,
  // Cloud / infra
  aws: Cloud,
  'amazon web services': Cloud,
  azure: Cloud,
  gcp: Cloud,
  'google cloud': Cloud,
  kubernetes: Cloud,
  docker: Cloud,
  terraform: Cloud,
  ansible: Cloud,
  jenkins: Cloud,
  'ci/cd': Cloud,
  devops: Cloud,
  grpc: Cloud,
  kafka: Cloud,
  // Databases
  sql: Database,
  postgresql: Database,
  postgres: Database,
  mysql: Database,
  mongodb: Database,
  redis: Database,
  elasticsearch: Database,
  dynamodb: Database,
  nosql: Database,
  // Frontend / product
  react: Browser,
  vue: Browser,
  angular: Browser,
  'next.js': Browser,
  css: Browser,
  html: Browser,
  figma: Browser,
  'ui/ux': Browser,
  'ux design': Browser,
  // Data / analytics
  sql_data: Database,
  'data analysis': ChartLineUp,
  'data science': ChartLineUp,
  'data engineering': ChartLineUp,
  pandas: ChartLineUp,
  spark: ChartLineUp,
  tableau: ChartLineUp,
  'power bi': ChartLineUp,
  // AI / ML
  ml: Brain,
  'machine learning': Brain,
  'deep learning': Brain,
  ai: Brain,
  'artificial intelligence': Brain,
  llm: Brain,
  llms: Brain,
  'large language models': Brain,
  nlp: Brain,
  'natural language processing': Brain,
  'computer vision': Brain,
  'generative ai': Brain,
  genai: Brain,
  tensorflow: Brain,
  pytorch: Brain,
  'reinforcement learning': Brain,
  'fine-tuning': Brain,
  finetuning: Brain,
  prompting: MagicWand,
  'prompt engineering': MagicWand,
  agent: Robot,
  agents: Robot,
  agentic: Robot,
  'agent workflows': Robot,
  retrieval: Detective,
  rag: Detective,
  'retrieval augmented generation': Detective,
  // Systems / infra
  'production systems': Stack,
  'production ml': Stack,
  'distributed systems': Stack,
  microservices: Stack,
  'system design': Stack,
  scalability: Gauge,
  'high availability': Gauge,
  performance: Gauge,
  'performance tuning': Gauge,
  optimization: Gauge,
  'event-driven': Broadcast,
  'pub/sub': Broadcast,
  streaming: Broadcast,
  git: GitBranch,
  github: GitBranch,
  gitlab: GitBranch,
  'version control': GitBranch,
  security: ShieldCheck,
  cybersecurity: ShieldCheck,
  compliance: ShieldCheck,
  encryption: ShieldCheck,
  // Debugging / quality
  debugging: Detective,
  'debugging pipelines': Detective,
  troubleshooting: Detective,
  observability: Detective,
  monitoring: Gauge,
  logging: Detective,
  testing: Target,
  qa: Target,
  'quality assurance': Target,
  'test automation': Target,
  // Design
  'graphic design': PaintBrush,
  'visual design': PaintBrush,
  branding: PaintBrush,
  // Soft skills
  communication: ChatCircleDots,
  'clinical communication': ChatCircleDots,
  'stakeholder communication': ChatCircleDots,
  leadership: UsersThree,
  teamwork: UsersThree,
  collaboration: UsersThree,
  'cross-functional collaboration': UsersThree,
  mentoring: UsersThree,
  agile: UsersThree,
  scrum: UsersThree,
  'project management': UsersThree,
  'problem solving': Lightning,
  negotiation: Scales,
  dbt: Database,
  'embedded systems': Stack,
  'operating systems': Stack,
  databricks: ChartLineUp,
};

const LANGUAGE_KEYWORDS = ['java', 'python', 'script', 'kotlin', 'swift', 'scala', 'ruby', 'rust', 'golang', 'php', 'c++', 'c#'];
const CLOUD_KEYWORDS = ['aws', 'azure', 'cloud', 'kubernetes', 'docker', 'terraform', 'devops', 'ci/cd', 'infra', 'kafka', 'grpc'];
const DATABASE_KEYWORDS = ['sql', 'database', 'db', 'redis', 'mongo', 'postgres', 'mysql', 'elastic'];
// 'design' is deliberately not here: it is a real whole word in unrelated values ("Electrical
// design"), so design values are covered by the exact SKILL_ICONS entries ('ux design', etc.).
// 'ui' and 'ux' are safe now that matching is by whole token ("Linux" and "building" no longer hit).
const FRONTEND_KEYWORDS = ['react', 'vue', 'angular', 'frontend', 'front-end', 'css', 'html', 'ui', 'ux', 'figma'];
const DATA_KEYWORDS = ['data', 'analytics', 'statistics', 'analysis'];
const AI_ML_KEYWORDS = ['ml', 'ai', 'llm', 'model', 'neural', 'nlp', 'vision', 'generative'];
const AGENT_KEYWORDS = ['agent', 'automation', 'workflow'];
const SYSTEMS_KEYWORDS = ['system', 'distributed', 'production', 'infrastructure', 'architecture', 'scalab', 'pipeline'];
const DEBUG_KEYWORDS = ['debug', 'debugging', 'troubleshoot', 'observab', 'incident', 'root cause'];
const SOFT_SKILL_KEYWORDS = ['communicat', 'leadership', 'teamwork', 'collaborat', 'mentor', 'agile', 'scrum', 'management', 'stakeholder'];

// Stems that should match any token they are a prefix of (communicating, communication,
// collaborate, collaborative, scalable, scalability, observability, troubleshooting, ...), not
// just an exact token. Everything else below matches whole tokens (or, for a multi-word keyword,
// a consecutive run of tokens) so a keyword like 'design' or 'ai' can never match as a mere
// substring of an unrelated word ("Electrical design", "Attention to detail").
const PREFIX_STEMS = new Set(['communicat', 'collaborat', 'scalab', 'observab', 'troubleshoot', 'script']);

/**
 * Splits a value into lowercase word tokens. Alphanumerics plus `+` and `#` stay glued together
 * so 'c++' and 'c#' remain single tokens; every other separator (whitespace, commas, slashes,
 * hyphens, parentheses, etc.) splits, so 'C/C++' becomes ['c', 'c++'] and 'front-end' / 'ci/cd'
 * become two tokens that still match the same keyword as a sequence.
 */
function tokenize(value: string): string[] {
  return value
    .toLowerCase()
    .trim()
    .split(/[^a-z0-9+#]+/)
    .filter(Boolean);
}

function keywordMatchesTokens(keyword: string, tokens: string[]): boolean {
  if (PREFIX_STEMS.has(keyword)) {
    return tokens.some((token) => token.startsWith(keyword));
  }
  const keywordTokens = tokenize(keyword);
  if (keywordTokens.length === 0) return false;
  if (keywordTokens.length === 1) {
    // A plain trailing 's' also matches ('model' covers "Language models", 'pipeline' covers "Data
    // pipelines"), except for 'system': "ERP systems", "HVAC systems" and "Electrical systems" are
    // not software stacks, so the real ones are exact SKILL_ICONS entries instead.
    const single = keywordTokens[0];
    return tokens.some((token) => token === single || (single !== 'system' && token === `${single}s`));
  }
  // Multi-word keyword (e.g. 'root cause', 'front-end'): match a consecutive run of tokens.
  for (let start = 0; start <= tokens.length - keywordTokens.length; start += 1) {
    if (keywordTokens.every((keywordToken, offset) => tokens[start + offset] === keywordToken)) {
      return true;
    }
  }
  return false;
}

function matchesAny(tokens: string[], keywords: string[]): boolean {
  return keywords.some((keyword) => keywordMatchesTokens(keyword, tokens));
}

/**
 * Resolves a skill/technology facet value to an icon: exact curated match first (the large
 * SKILL_ICONS map above, matched against the full trimmed value), then a category-keyword
 * fallback bucket matched by whole word/word-prefix, not substring, so "Attention to detail"
 * cannot match the AI bucket's 'ai' through "detail", "Electrical systems" cannot match the
 * systems bucket through "systems" vs. singular 'system', and so on (see PREFIX_STEMS above for
 * the deliberate exceptions). Returns null; no icon at all; only for values that genuinely don't
 * fit any bucket, so a chip never gets a meaningless generic glyph; the goal per the design
 * review is real icons wherever a value's category can be inferred, not maximal coverage at the
 * cost of accuracy.
 */
export function getSkillIcon(value: string): SkillIconComponent | null {
  const lower = value.toLowerCase().trim();
  const exact = SKILL_ICONS[lower];
  if (exact) return exact;

  const tokens = tokenize(value);

  if (matchesAny(tokens, LANGUAGE_KEYWORDS)) return Code;
  if (matchesAny(tokens, CLOUD_KEYWORDS)) return Cloud;
  if (matchesAny(tokens, DATABASE_KEYWORDS)) return Database;
  if (matchesAny(tokens, FRONTEND_KEYWORDS)) return Browser;
  if (matchesAny(tokens, AI_ML_KEYWORDS)) return Brain;
  if (matchesAny(tokens, AGENT_KEYWORDS)) return Robot;
  if (matchesAny(tokens, DEBUG_KEYWORDS)) return Detective;
  if (matchesAny(tokens, SYSTEMS_KEYWORDS)) return Stack;
  if (matchesAny(tokens, DATA_KEYWORDS)) return ChartLineUp;
  if (matchesAny(tokens, SOFT_SKILL_KEYWORDS)) return UsersThree;

  return null;
}
